<template>
  <div v-if="shouldDisplayUpgrade">
    <component
      :is="comp"
      class="shadow-long rounded-full body-1-bold fixed left-1/2 z-[1] hidden w-max -translate-x-1/2 items-center px-12 py-8"
      :class="{
        'bg-static-default-hi text-static-default-low': isLoading,
        'bg-surface-default-mid text-static-brand-hi hover:bg-static-default-mid':
          canUpgrade,
        'bg-static-info-mid text-static-brand-hi cursor-default': isUpgraded,
        [isUpgradedLabelClassnames]: isUpgraded,
        [$style.animateShimmer]: isUpgraded,
        [$style.slideInFromTop]: applyAnimation,
      }"
      :style="pillStyle"
      @click="handlePillClick"
    >
      <!-- This div is necessary to put the right color on the spinner -->
      <div v-if="isLoading" class="text-static-default-hi mr-4">
        <RevSpinner aria-hidden />
      </div>
      <IconCheckWithSparkles v-if="canUpgrade || isUpgraded" class="mr-4" />
      <div
        :class="{
          [$style.animateRainbow]: isLoading,
          [isLoadingLabelClassnames]: isLoading,
        }"
      >
        {{ label }}
      </div>
      <IconCrossSmall
        v-if="isUpgraded"
        class="ml-4 cursor-pointer"
        @click="handleUpgradedClose"
      />
    </component>
    <UpgradeModal :pickers @upgrade-accepted="handleUpgradeAccepted" />
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute, useState } from '#imports'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import { openModal } from '@ds/components/ModalBase'
import { RevSpinner } from '@ds/components/Spinner'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import { useResizeObserver } from '@vueuse/core'

import {
  CATEGORIES_WITH_UPGRADE,
  MODAL_NAMES,
} from '~/scopes/product/constants'
import { useCreateLinkFromPicker } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/composables/useCreateLinkFromPicker/useCreateLinkFromPicker'
import { useAddToCartOverlay } from '~/scopes/product-core/composables/stores/useAddToCartOverlay/useAddToCartOverlay'

import { useShowStickyBarTop } from '../StickyBarTop/composables/useShowStickyBarTop'
import { useShowStickyBarTopSmall } from '../StickyBarTopSmall/composables/useShowStickyBarTopSmall'

import translations from './UpgradeForLess.translations'
import UpgradeModal from './components/UpgradeModal/UpgradeModal.vue'

const props = defineProps<{
  product: GetProductResponse
  pickers: GetPickersResponse
}>()

const emit = defineEmits<{
  (
    e: 'upgrade-accepted',
    pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
  ): void
}>()

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()
const experiments = useExperiments()
const { showBar: showStickyBarTop } = useShowStickyBarTop()
const { showBar: showStickyBarTopSmall } = useShowStickyBarTopSmall()
const { createUpgradeLink } = useCreateLinkFromPicker()
const { shouldShowAddToCartOverlay } = useAddToCartOverlay()

const pillStyle = ref<{ [key: string]: string } | undefined>(undefined)
const applyAnimation = ref(false)
const isLoading = useState('upgrade-for-less-is-loading', () => false)
const canUpgrade = useState('upgrade-for-less-can-upgrade', () => false)
const isUpgraded = useState('upgrade-for-less-is-upgraded', () => false)
const automaticModalOpening = useState(
  'upgrade-for-less-automatic-modal-opening',
  () => true,
)
const firstLoad = useState('upgrade-for-less-first-load', () => true)
let timeoutUpgrade = null as ReturnType<typeof setTimeout> | null
let timeoutPosition = null as ReturnType<typeof setTimeout> | null

const isLoadingLabelClassnames = tw`bg-[linear-gradient(90deg,#5b5d62,#4055bf,#5b5d62)] bg-clip-text text-transparent bg-[length:200%_auto]`

const isUpgradedLabelClassnames = tw`overflow-hidden after:absolute after:top-0 after:left-0 after:h-full after:w-full after:-translate-x-full after:content-['']`

const shouldDisplayUpgrade = computed(() => {
  return (
    (isUpgraded.value || props.pickers.upgrade) &&
    experiments['experiment.ppUpgradeForLess'] === 'withUpgrade' &&
    CATEGORIES_WITH_UPGRADE.includes(props.product.tracking.categoryId)
  )
})

const label = computed(() => {
  if (isLoading.value) {
    return i18n(translations.loading)
  }
  if (isUpgraded.value) {
    return i18n(translations.upgraded)
  }

  if (canUpgrade.value) {
    return i18n(translations.canUpgrade)
  }

  return ''
})

const comp = computed(() => {
  return isLoading.value ? 'div' : 'button'
})

async function handleUpgradeAccepted() {
  canUpgrade.value = false
  isUpgraded.value = true

  emit('upgrade-accepted', props.pickers.upgrade?.pickerFields)

  await navigateTo(createUpgradeLink(props.pickers.upgrade))
}

function handlePillClick() {
  if (isLoading.value) {
    return
  }

  if (canUpgrade.value) {
    openModal(MODAL_NAMES.UPGRADE_FOR_LESS)
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'open_modal',
    })
  }

  if (isUpgraded.value) {
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'smart_upgrade_applied',
    })
  }
}

function handleUpgradedClose() {
  isUpgraded.value = false
}

onMounted(() => {
  const stickyBarTop = shallowRef<HTMLElement | null>(
    document.querySelector('[id="sticky-bar-top"]') as HTMLElement | null,
  )
  const stickyBarTopSmall = shallowRef<HTMLElement | null>(
    document.querySelector('[id="sticky-bar-top-small"]') as HTMLElement | null,
  )

  // On sticky bar resize we want to make sure that the pill is already under the sticky.
  useResizeObserver(stickyBarTop, (entries) => {
    for (const entry of entries) {
      pillStyle.value = {
        top: `${entry.target.clientHeight + 8}px`,
        display: 'flex',
      }
    }
  })
  useResizeObserver(stickyBarTopSmall, (entries) => {
    for (const entry of entries) {
      pillStyle.value = {
        top: `${entry.target.clientHeight + 8}px`,
        display: 'flex',
      }
    }
  })
})

onBeforeUnmount(() => {
  if (timeoutUpgrade) {
    clearTimeout(timeoutUpgrade)
  }
  if (timeoutPosition) {
    clearTimeout(timeoutPosition)
  }
})
// const stickyBarTop = ref<HTMLElement | null>(null)

watch(showStickyBarTop, () => {
  const stickyBarTop = document.querySelector<HTMLElement>(
    '[id="sticky-bar-top"]',
  )
  // When the sticky is not display we don't want to display the pill
  // We want to make sure that the pill is always under the   sticky.
  if (showStickyBarTop.value && stickyBarTop) {
    // On sticky bar resize we want to make sure that the pill is already under the sticky.
    if (firstLoad.value) {
      useResizeObserver(stickyBarTop, (entries) => {
        for (const entry of entries) {
          pillStyle.value = {
            top: `${entry.target.clientHeight + 8}px`,
            display: 'flex',
          }
        }
      })
    }

    pillStyle.value = {
      display: 'flex',
      top: `${stickyBarTop.clientHeight + 8}px`,
    }

    if (firstLoad.value && props.pickers.upgrade) {
      isLoading.value = true
      timeoutUpgrade = setTimeout(() => {
        firstLoad.value = false
        isLoading.value = false
        canUpgrade.value = true
      }, 2300)
    }

    return
  }

  pillStyle.value = { display: 'none' }
})

watch(showStickyBarTopSmall, () => {
  const stickyBarTopSmall = document.querySelector<HTMLElement>(
    '[id="sticky-bar-top-small"]',
  )

  // When the sticky is not display we don't want to display the pill
  // We want to make sure that the pill is always under the sticky.
  if (showStickyBarTopSmall.value && stickyBarTopSmall) {
    useResizeObserver(stickyBarTopSmall, (entries) => {
      for (const entry of entries) {
        pillStyle.value = {
          top: `${entry.target.clientHeight + 8}px`,
          display: 'flex',
        }
      }
    })

    pillStyle.value = {
      display: 'flex',
      top: `${stickyBarTopSmall.clientHeight + 8}px`,
    }

    if (firstLoad.value && props.pickers.upgrade) {
      isLoading.value = true
      timeoutUpgrade = setTimeout(() => {
        firstLoad.value = false
        isLoading.value = false
        canUpgrade.value = true
      }, 2300)
    }

    return
  }

  pillStyle.value = { display: 'none' }
})

watch(
  pillStyle,
  () => {
    applyAnimation.value = true

    timeoutPosition = setTimeout(() => {
      applyAnimation.value = false
    }, 1000)
  },
  { immediate: true },
)

// TO REFACTOR
// with same productID -> grade or other variants
// with different productID -> grade or other variants
watch(
  () => props.pickers,
  (newValue, oldValue) => {
    if (timeoutUpgrade) {
      clearTimeout(timeoutUpgrade)
    }

    // Detects if the user has upgraded the offer.
    if (!newValue?.upgrade && oldValue?.upgrade && isUpgraded.value) {
      return
    }

    isUpgraded.value = false

    // Detects first load and if we have a upgrade available for the first load
    // When we arrived on the page with no upgrade available we considere that the first load happened.
    // Otherwise it won't opened the modal automatically if the next picker has an upgrade.
    if (!oldValue && !newValue?.upgrade) {
      firstLoad.value = false

      return
    }

    // If no upgrade in new value -> don't do anything.
    if (!newValue?.upgrade) {
      return
    }

    isLoading.value = true
    canUpgrade.value = false
    timeoutUpgrade = setTimeout(() => {
      isLoading.value = false
      canUpgrade.value = true

      if (
        !firstLoad.value &&
        automaticModalOpening.value &&
        route.query.pickerClicked &&
        !shouldShowAddToCartOverlay.value
      ) {
        setTimeout(() => {
          automaticModalOpening.value = false
          firstLoad.value = false
          openModal(MODAL_NAMES.UPGRADE_FOR_LESS)
          trackClick({
            zone: 'upgrade_for_less_modal',
            value: 'open_modal_automatic',
          })
        }, 800)
      }
    }, 2300)
  },
  { immediate: true },
)
</script>

<style module lang="css" scoped>
.slideInFromTop {
  animation: slideDown 500ms ease-out forwards;
}

@keyframes slideDown {
  from {
    transform: translateX(-50%) translateY(-100px);
  }

  to {
    transform: translateX(-50%) translateY(0);
  }
}

.animateShimmer::after {
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 80%
  );
  animation: shimmer 2s 2s forwards;
}

@keyframes shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

.animateRainbow {
  animation: rainbow 1s linear infinite;
}

@keyframes rainbow {
  from {
    background-position: 200% center;
  }

  to {
    background-position: 0% center;
  }
}
</style>
